import dayjs from "dayjs";
import { TFunction } from "i18next";
import { CustomDate } from "@/types/schemas/person";
import { allowedEmailDomains } from "../../constants/emails";
import { allowedPhonePrefixes } from "../../constants/mobilePhone";
import { Contract } from "@/types/contract";

export const isMobilePhoneValid = (value: string, code: string) => {
  if (code !== "CZ") {
    return value.length >= 6 && value.length <= 15;
  }
  const prefix = value.slice(0, 3);

  if (allowedPhonePrefixes.includes(prefix)) {
    return value.length === 9 && (value[0] === "6" || value[0] === "7");
  }
  return false;
};

const companiesNameParts =
  /(?:^|\s)(?:s\.?\s*r\.?\s*o\.?|spol\.?\s*s\s*r\.?\s*o\.?|a\.?\s*s\.?|k\.?\s*s\.?|v\.?\s*o\.?\s*s\.?|z\.?\s*s\.?|o\.?\s*p\.?\s*s\.?|s\.?\s*p\.?|spol|gmbh|ltd|inc|corp|město|městys|obec|vesnice|úřad|magistrát|samospráva|spolek|družstvo|nadace|ústav|organizace|sdružení|firma|společnost|holding|group|servis|prodejna|obchod|autodíly|restaurace|hostinec|potraviny|večerka|lékárna|škola|školka|stavebniny|realit(?:y|ka)|svj|městská|část|realitní|kancelář|byt|bytový|podnik|company|enterprise|gymnázium|univerzita|nemocnice|poliklinika|domov|centrum|služba|nadační|fond|charita|technické|služby|vodárny|kanalizace|teplárna|energie|provoz|facility|management|develop|invest|stavby|construction)(?:$|\s)/i;

export const hasCompanyNamePart = (value: string) => companiesNameParts.test(value.trim());

export const isNameValid = (value: string, maxLen: number) => {
  return value.length > 1 && value.length < maxLen && new RegExp("^[\\p{L} \\p{M}'´`‘’-]+$", "u").test(value);
};

export const isBirthDateValid = (value: CustomDate) => {
  return value !== null && dayjs().diff(dayjs(value), "years") >= 18 && dayjs().diff(dayjs(value), "years") <= 120;
};

export const isDeathDateValid = (value: CustomDate, contract: Contract) => {
  const birthDate = contract.customer.birthDate;
  return value !== null && dayjs(value).isAfter(dayjs(birthDate));
};

export const isEmailValid = (value: string) => {
  const match = /^[\w.\-+]+@\w+([.-]?\w+)*(\.\w{2,13})+$/.exec(value);
  if (!match) {
    return false;
  }
  const firstLevelDomain = match[2]!.slice(1).toUpperCase();
  return allowedEmailDomains.includes(firstLevelDomain);
};

export const nameValidator = (maxLen: number, invalidMessage: string, invalidCompanyMessage: string, value: string) => {
  const trimmed = value.trim();
  if (hasCompanyNamePart(trimmed)) {
    return invalidCompanyMessage;
  }
  if (!isNameValid(trimmed, maxLen)) {
    return invalidMessage;
  }
  return null;
};

export const getPersonValidations = (t: TFunction<"validations">) => ({
  email: (value: string) => (isEmailValid(value) ? null : t("invalidEmail")),
  mobilePhone: (value: string, mobileCountryCode: string) =>
    !value ? t("emptyMobileNumber") : isMobilePhoneValid(value, mobileCountryCode) ? null : t("invalidMobileNumber"),
  name: (value: string) => nameValidator(20, t("invalidName"), t("customerNameInvalid"), value),
  surname: (value: string) => nameValidator(40, t("invalidSurname"), t("customerNameInvalid"), value),
  birthDate: (value: CustomDate) => (isBirthDateValid(value) ? null : t("invalidBirthDate")),
});
